import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/api";
import { useLang } from "@/i18n";

export default function Settings() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const CATS = ["U6", "U8", "U10", "U12", "U14"];
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
    api.get("/training-plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const getPlan = (c) => plans.find((p) => p.category === c) || { category: c, focus: "", outcomes: "" };
  const setPlan = (c, field, val) => setPlans([...plans.filter((p) => p.category !== c), { ...getPlan(c), [field]: val }]);
  const savePlans = async () => {
    try {
      await api.put("/training-plans", { plans: CATS.map(getPlan) });
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/settings", {
        registration_fee: Number(form.registration_fee),
        monthly_fee: Number(form.monthly_fee),
        monthly_scheme_1: Number(form.monthly_scheme_1 || 0),
        monthly_scheme_2: Number(form.monthly_scheme_2 || 0),
        monthly_scheme_3: Number(form.monthly_scheme_3 || 0),
        match_fee: Number(form.match_fee),
        allowance_rate: Number(form.allowance_rate),
        registration_gift: form.registration_gift,
        fb_page_id: form.fb_page_id,
        fb_page_token: form.fb_page_token,
        site_phone: form.site_phone,
        site_email: form.site_email,
        site_address: form.site_address,
        site_tagline_ms: form.site_tagline_ms,
        site_tagline_en: form.site_tagline_en,
      });
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  if (!form) return <p className="text-slate-500">{t("loading")}</p>;

  const fields = [
    { key: "registration_fee", label: t("regFee"), type: "number", testid: "set-reg-fee" },
    { key: "monthly_fee", label: t("monthlyFee"), type: "number", testid: "set-monthly-fee" },
    { key: "monthly_scheme_1", label: t("schemeFee1"), type: "number", testid: "set-scheme-1" },
    { key: "monthly_scheme_2", label: t("schemeFee2"), type: "number", testid: "set-scheme-2" },
    { key: "monthly_scheme_3", label: t("schemeFee3"), type: "number", testid: "set-scheme-3" },
    { key: "match_fee", label: t("matchFee"), type: "number", testid: "set-match-fee" },
    { key: "allowance_rate", label: t("allowanceRate"), type: "number", testid: "set-allowance-rate" },
    { key: "registration_gift", label: t("regGift"), type: "text", testid: "set-reg-gift" },
    { key: "fb_page_id", label: t("fbPageId"), type: "text", testid: "set-fb-page-id" },
    { key: "fb_page_token", label: t("fbToken"), type: "password", testid: "set-fb-token" },
    { key: "site_phone", label: t("sitePhone"), type: "text", testid: "set-site-phone" },
    { key: "site_email", label: t("siteEmail"), type: "text", testid: "set-site-email" },
    { key: "site_address", label: t("siteAddress"), type: "text", testid: "set-site-address" },
    { key: "site_tagline_ms", label: t("siteTaglineMs"), type: "text", testid: "set-tagline-ms" },
    { key: "site_tagline_en", label: t("siteTaglineEn"), type: "text", testid: "set-tagline-en" },
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("settings")}</h1>
      <form onSubmit={save} className="gfa-card max-w-2xl space-y-5 p-6">
        {fields.map((f) => (
          <div key={f.key}>
            <Label>{f.label}</Label>
            <Input data-testid={f.testid} type={f.type} step={f.type === "number" ? "0.01" : undefined} value={form[f.key] ?? ""}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1" />
          </div>
        ))}
        <p className="text-xs text-slate-400">{lang === "ms" ? "Berita akan auto-post ke Facebook Page apabila Page ID & Token diisi." : "News will auto-post to your Facebook Page once Page ID & Token are set."}</p>
        <Button data-testid="settings-save-btn" disabled={busy} className="rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">
          {busy ? t("loading") : t("save")}
        </Button>
      </form>

      <div className="gfa-card max-w-2xl space-y-5 p-6" data-testid="training-plans-section">
        <h2 className="font-['Outfit'] text-lg font-bold text-[#165A2E]">{t("trainingPlan")} — U6 / U8 / U10 / U12 / U14</h2>
        {CATS.map((c) => (
          <div key={c} className="space-y-2 rounded-lg border border-slate-100 p-4">
            <p className="text-sm font-bold text-[#165A2E]">{c}</p>
            <div>
              <Label>{t("planFocus")}</Label>
              <Textarea data-testid={`plan-focus-${c}`} rows={2} value={getPlan(c).focus} onChange={(e) => setPlan(c, "focus", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>{t("planOutcomes")}</Label>
              <Textarea data-testid={`plan-outcomes-${c}`} rows={2} value={getPlan(c).outcomes} onChange={(e) => setPlan(c, "outcomes", e.target.value)} className="mt-1" />
            </div>
          </div>
        ))}
        <Button data-testid="plans-save-btn" onClick={savePlans} className="rounded-full bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D]">{t("save")}</Button>
      </div>
    </div>
  );
}
